import React, { useMemo } from "react";
import { Box, Typography, Paper, Divider, Tooltip } from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { MimshakData } from "../../interfaces/DTO/mimshakData";
import { FundSumDropMap } from "../../interfaces/DTO/simulation";
import { formatCurrency, formatPercentage } from "../../utils/namesFormater";
import { formatHandlerCodeUniversal } from "../../utils/handlerCodeUniversalFormatter";
import { ChangesDataUtils } from "./Reports/ChangesDataUtils";
import { getEffectiveSavingFee } from "../../services/userTask/simulationChangeDetectionUtil";

interface Props {
  original: MimshakData[];
  modified: MimshakData[];
  isModifiedSavingFee?: boolean;
  fundSumDropMap?: FundSumDropMap;
}

interface WeightedFeeResult {
  avg: number;
  excludedFund: number;
  excludedHandlers: string[];
}

const calculateWeightedFee = (modified: MimshakData[], original: MimshakData[], fundSumDropMap?: FundSumDropMap): WeightedFeeResult => {
  const originalMap = new Map(original.map((o) => [o.policy_number, o]));

  const eligibleRows: MimshakData[] = [];
  let excludedFund = 0;
  const excludedHandlerSet = new Set<string>();

  for (const row of modified) {
    const orig = originalMap.get(row.policy_number);
    if (!orig && row.saving_fee == null) continue;
    if (orig && row.saving_fee == null) {
      excludedFund += row.funds_sum ?? 0;
      if (row.handler_code) excludedHandlerSet.add(row.handler_code);
      continue;
    }
    eligibleRows.push(row);
  }

  const getEffectiveFund = (row: MimshakData) => {
    const additions = fundSumDropMap?.[row.policy_number];
    return (row.funds_sum ?? 0) + ChangesDataUtils.getTotalFundAddition(additions);
  };

  const totalFund = eligibleRows.reduce((acc, row) => acc + getEffectiveFund(row), 0);

  const avg = totalFund
    ? eligibleRows.reduce((sum, row) => {
        const orig = originalMap.get(row.policy_number);
        const fee = getEffectiveSavingFee(orig?.saving_fee, row.saving_fee, !!orig && orig.handler_code !== row.handler_code) ?? 0;
        return sum + getEffectiveFund(row) * fee;
      }, 0) / totalFund
    : 0;

  return { avg, excludedFund, excludedHandlers: Array.from(excludedHandlerSet) };
};

// Per-policy saving: each policy's funds_sum × its own fee difference.
// More accurate than (totalFund × avgDiff) because not every shekel
// experiences the portfolio-average fee change.
const calculatePerPolicySavings = (modified: MimshakData[], original: MimshakData[]) => {
  const originalMap = new Map(original.map((o) => [o.policy_number, o]));
  let annual = 0;

  for (const row of modified) {
    // Skip policies with no fee configured after the change
    if (row.saving_fee == null) continue;

    const orig = originalMap.get(row.policy_number);
    const capital = row.funds_sum ?? 0;

    if (!orig) continue;

    const origFee = orig.saving_fee ?? 0;
    const feeAfter = getEffectiveSavingFee(orig.saving_fee, row.saving_fee, orig.handler_code !== row.handler_code) ?? origFee;
    annual += capital * (origFee - feeAfter) / 100;
  }

  return { annual, monthly: annual / 12 };
};

const FeeSavingsComparison: React.FC<Props> = ({ original, modified, isModifiedSavingFee, fundSumDropMap }) => {
  const { originalAvg, modifiedAvg, excludedFund, excludedHandlers, annual, monthly } = useMemo(() => {
    const originalResult = calculateWeightedFee(original, original);
    const modifiedResult = calculateWeightedFee(modified, original, fundSumDropMap);
    const originalAvg = originalResult.avg;
    const modifiedAvg = modifiedResult.avg;
    const excludedFund = modifiedResult.excludedFund;
    const excludedHandlers = modifiedResult.excludedHandlers;

    const { annual, monthly } = calculatePerPolicySavings(modified, original);

    return { originalAvg, modifiedAvg, excludedFund, excludedHandlers, annual, monthly };
  }, [original, modified, fundSumDropMap]);

  const hasExcluded = excludedFund > 0;

  return (
    <Paper sx={{ p: 3, direction: "rtl" }}>
      <Typography variant="h6" gutterBottom>
        השוואת דמי ניהול משוקללים
      </Typography>

      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography>
          לפני שינוי: <strong>{formatPercentage(originalAvg)}</strong>
        </Typography>
        <Box display="flex" alignItems="center" gap={0.5}>
          {isModifiedSavingFee && (
            <Typography>
              אחרי שינוי: <strong>{formatPercentage(modifiedAvg)}</strong>
            </Typography>
          )}
          {hasExcluded && (
            <Tooltip
              title={
                <Box>
                  <Typography variant="body2" fontWeight="bold" gutterBottom>
                    סכום שהוצא מהחישוב: {formatCurrency(excludedFund)}
                  </Typography>
                  <Typography variant="body2">
                    סיבה: אין מדרגות דמי ניהול מוגדרות עבור{" "}
                    {excludedHandlers.map((h) => formatHandlerCodeUniversal(h)).join(", ")}
                  </Typography>
                </Box>
              }
              arrow
            >
              <WarningAmberIcon sx={{ color: "orange", fontSize: 20, cursor: "pointer" }} />
            </Tooltip>
          )}
        </Box>
      </Box>

      {isModifiedSavingFee && (
        <>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            חיסכון מוערך עבור סכום הכסף המקורי - {formatCurrency(original.reduce((acc, r) => acc + (r.funds_sum ?? 0), 0))}
          </Typography>
          <Box display="flex" alignItems="center" gap={0.5}>
            <Typography variant="subtitle1">
              חיסכון חודשי: <span dir="ltr"><strong>{formatCurrency(monthly)}</strong></span>
            </Typography>
            {hasExcluded && (
              <Tooltip
                title={
                  <Box>
                    <Typography variant="body2" fontWeight="bold" gutterBottom>
                      החיסכון חלקי בלבד
                    </Typography>
                    <Typography variant="body2">
                      פוליסות בסך {formatCurrency(excludedFund)} אצל{" "}
                      {excludedHandlers.map((h) => formatHandlerCodeUniversal(h)).join(", ")}{" "}
                      הוצאו מהחישוב כי אין מדרגות דמי ניהול מוגדרות עבורן
                    </Typography>
                  </Box>
                }
                arrow
              >
                <WarningAmberIcon sx={{ color: "orange", fontSize: 20, cursor: "pointer" }} />
              </Tooltip>
            )}
          </Box>
          <Typography variant="subtitle1">
            חיסכון שנתי: <span dir="ltr"><strong>{formatCurrency(annual)}</strong></span>
          </Typography>
<Typography variant="caption" color="text.secondary" display="block" mt={2}>
            * החיסכון מחושב לפי הצבירה הנוכחית
          </Typography>
        </>
      )}
    </Paper>
  );
};

export default FeeSavingsComparison;
